/*
 * @Descripttion: 合规平台（审方/分析类）本地服务通知 —— 开处方等页面通用
 * @version:
 * @Author: ZQian
 * @email:
 * @Date: 2026-09-21
 * @LastEditors: ZQian
 * @LastEditTime: 2026-09-21
 */

/**
 * 合规平台本地服务通知（通用方法）。
 *
 * 流程：
 * 1. 判断本地服务是否开启且可通信，不通则直接静默放弃（后端接口也不调用）；
 * 2. 调后端 /blade-reminder/compliancePlatforms/payload 获取需要发给本地服务的报文；
 * 3. 将报文（method/url/headers/body）透传给本地服务。
 *
 * 全程不会产生任何异常、不会触发全局错误提示，绝不影响主流业务。
 */
import { setCommonHeaders } from '../app/applicationSlice'
import { send } from '../services/request'
import {
  getCompliancePlatformsPayload,
  CompliancePlatformPayloadParams,
  CompliancePlatformPayload,
} from '../services/remind'
import {
  callLocalService,
  isPortAlive,
  isLocalServiceAlive,
  LocalServiceFailReason,
  LocalServiceResult,
} from './localService'

/**
 * Redux state 取值函数，由 app/store 在创建 store 后注入。
 *
 * 这里刻意不直接 `import { store } from '../app/store'`：
 * 否则一旦有 slice 引用本模块，就会形成
 * `store → slice → complianceReminder → store` 的循环依赖，
 * 当 slice 早于 store 被求值时，configureStore 拿到的 slice reducer 还是 undefined，
 * Redux 会静默丢弃该 slice（仅告警 No reducer provided for key "xxx"），
 * 导致运行时 `state.xxx` 为 undefined、页面报 TypeError。
 */
let complianceStateGetter: (() => any) | undefined

/** 注入 Redux state 取值函数（由 app/store 调用，勿在业务代码里重复调用） */
export function setComplianceStateGetter(getState: () => any) {
  complianceStateGetter = getState
}

export interface SendCompliancePayloadParams extends CompliancePlatformPayloadParams {
  /** 调用本地服务时的 isNeedRes 请求头；不传时沿用后端报文 headers 里的值 */
  isNeedRes?: boolean | string
  /** 调用本地服务的超时时间 ms，默认 10000 */
  timeout?: number
  /** 成功回调：本地服务正常返回时触发，data 为本地服务的响应 */
  onSuccess?: (data?: any) => void
  /** 失败回调：本地服务未启动 / 后端取报文失败 / 本地调用失败等，仅用于排查日志 */
  onError?: (reason: LocalServiceFailReason, detail?: string) => void
  /** 完成回调：无论成败最后触发，用 result.ok 区分成败 */
  onComplete?: (result: LocalServiceResult) => void
}

/**
 * 通知本地合规分析服务（开处方等页面通用，fire-and-forget 安全）。
 *
 * 现有业务只需发起调用、不接返回值即可（内部绝不抛错、不会产生未处理的 Promise 拒绝）；
 * 后续需要处理本地服务返回数据时，通过 onSuccess 回调获取。
 *
 * @example
 * // 保存处方后通知本地服务做处方分析
 * sendCompliancePayload({
 *   businessType: 'recipe', // 取值与后端约定
 *   treatmentId: '21924656',
 *   recipeId: '192205485',
 *   isNeedRes: true,
 * })
 *
 * @example
 * // 需要处理返回 / 排查日志时
 * sendCompliancePayload({
 *   businessType: 'recipe',
 *   treatmentId,
 *   recipeId,
 *   onSuccess: (data) => console.log('本地服务返回', data),
 *   onError: (reason, detail) => console.warn('合规平台通知失败', reason, detail),
 * })
 */
export async function sendCompliancePayload(
  params: SendCompliancePayloadParams
): Promise<LocalServiceResult> {
  // fail 只构造结果，finish 统一分发回调（回调内部异常也吞掉，保证整体绝不报错）
  const fail = (reason: LocalServiceFailReason, detail?: string): LocalServiceResult => ({
    ok: false,
    reason,
    detail,
  })
  const finish = (result: LocalServiceResult): LocalServiceResult => {
    if (result.ok) {
      safeInvoke(params.onSuccess, result.data)
    } else {
      safeInvoke(params.onError, result.reason, result.detail)
    }
    safeInvoke(params.onComplete, result)
    return result
  }

  try {
    // 0. 必传参数校验：treatmentId 缺失则静默放弃
    if (
      params.treatmentId === undefined ||
      params.treatmentId === null ||
      params.treatmentId === ''
    ) {
      return finish(fail('invalidParams', 'treatmentId 必传'))
    }

    // 1. 先判断本地服务是否开启且可通信，不通则直接放弃（不调后端接口）
    const alive = await isLocalServiceAlive()
    if (!alive) {
      return finish(fail('unreachable', '本地服务不可通信（健康检查无正常返回），跳过本次通知'))
    }

    

    // 2. 调后端接口获取报文（携带与全局一致的鉴权头；失败静默，不弹全局错误）
    const getState = complianceStateGetter
    if (!getState) {
      return finish(fail('unknown', '未注入 state 取值函数（setComplianceStateGetter）'))
    }
    let payload: CompliancePlatformPayload | undefined
    try {
      payload = await send<CompliancePlatformPayload>(
        setCommonHeaders(
          getCompliancePlatformsPayload({
            businessType: params.businessType,
            treatmentId: params.treatmentId,
            recipeId: params.recipeId,
            recipeItemId: params.recipeItemId,
          }),
          getState
        )
      )
    } catch (e) {
      return finish(fail('backendError', `获取报文接口失败: ${String(e)}`))
    }
    if (!payload || !payload.url) {
      return finish(fail('backendError', '后端未返回有效报文（缺少 url）'))
    }
    if (payload.body === undefined || payload.body === null) {
      return finish(fail('backendError', '后端未返回有效报文（缺少 body）'))
    }

    // 3. 拆分后端返回的完整 url（如 http://127.0.0.1:8090/analysis/recipeAnalysis）
    const split = splitFullUrl(payload.url)
    if (!split) {
      return finish(fail('backendError', `报文 url 无法解析: ${payload.url}`))
    }

    // 4. 报文透传给本地服务：method/headers/contentType 用后端返回值，
    //    入参 isNeedRes 优先级高于报文 headers 里的 isNeedRes
    const headers: Record<string, string> = { ...(payload.headers || {}) }
    if (payload.contentType) {
      headers['Content-Type'] = payload.contentType
    }
    if (params.isNeedRes !== undefined) {
      headers.isNeedRes =
        typeof params.isNeedRes === 'boolean' ? String(params.isNeedRes) : params.isNeedRes
    }

    const result = await callLocalService({
      host: split.host,
      path: split.path,
      method: normalizeMethod(payload.method),
      body: payload.body,
      headers,
      timeout: params.timeout,
    })
    return finish(result)
  } catch (e) {
    return finish(fail('unknown', String(e)))
  }
}

/** 将后端返回的完整 url 拆成本地服务的 host + path；无法解析时返回 undefined */
function splitFullUrl(url: string): { host: string; path: string } | undefined {
  const match = /^(https?:\/\/[^/]+)(.*)$/.exec(url.trim())
  if (!match) {
    return undefined
  }
  return { host: match[1], path: match[2] || '/' }
}

/** 规范化本地服务请求方法，非法值回退 POST */
function normalizeMethod(method?: string): 'GET' | 'POST' | 'PUT' | 'DELETE' {
  const upper = (method || '').toUpperCase()
  return upper === 'GET' || upper === 'PUT' || upper === 'DELETE' ? upper : 'POST'
}

/** 安全执行回调：回调内部抛出的异常同样被吞掉，保证 fire-and-forget 场景不产生未处理报错 */
function safeInvoke(fn: ((...args: any[]) => void) | undefined, ...args: any[]): void {
  if (!fn) {
    return
  }
  try {
    fn(...args)
  } catch (e) {
    // 吞掉回调内部异常，不影响主业务
  }
}
